(() => {
  if (window.AWCompatibilityDemandAnalytics?.version) return;

  const VERSION = 'AW-COMPAT-DEMAND-P2-V1-20260914';
  const sent = new Set();
  const rootState = new WeakMap();
  let analyticsPromise = null;
  let predictivePanelWatcher = null;

  const settingsNode = () => document.querySelector('[data-aw-predictive-search-settings]');
  const clean = (value = '', limit = 160) => String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, limit);
  const normalized = (value = '') => clean(value, 200).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const pathOf = (href = '') => {
    try { return new URL(href, location.href).pathname.slice(0, 180); }
    catch (_) { return ''; }
  };

  const ensureAnalytics = () => {
    if (window.AWAnalytics?.track) return Promise.resolve(window.AWAnalytics);
    if (analyticsPromise) return analyticsPromise;
    const src = settingsNode()?.dataset.analyticsSrc || '';
    if (!src) return Promise.resolve(null);

    analyticsPromise = new Promise((resolve) => {
      const absolute = new URL(src, location.href).href;
      const existing = [...document.scripts].find((script) => script.src === absolute);
      const wait = (attempt = 0) => {
        if (window.AWAnalytics?.track || attempt >= 24) {
          resolve(window.AWAnalytics || null);
          return;
        }
        window.setTimeout(() => wait(attempt + 1), 125);
      };
      if (!existing) {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.dataset.awCompatibilityDemandAnalytics = 'true';
        script.addEventListener('load', () => wait(), { once: true });
        script.addEventListener('error', () => resolve(null), { once: true });
        document.head.appendChild(script);
      } else {
        wait();
      }
    });
    return analyticsPromise;
  };

  const track = (name, payload = {}, dedupeKey = '') => {
    if (dedupeKey && sent.has(dedupeKey)) return;
    if (dedupeKey) sent.add(dedupeKey);
    ensureAnalytics().then(() => {
      try {
        window.AWAnalytics?.track?.(name, {
          analytics_mode: 'observe_only',
          analytics_surface: 'compatibility_demand',
          governance_version: VERSION,
          ...payload
        });
      } catch (_) {}
    });
  };

  const parseCount = (node) => {
    const match = clean(node?.textContent || '').match(/(\d[\d\s]*)/);
    return match ? Number(match[1].replace(/\s/g, '')) : 0;
  };

  const initZeroRecovery = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-aw-zero-recovery]')) roots.push(scope);
    roots.push(...scope.querySelectorAll?.('[data-aw-zero-recovery]') || []);

    roots.forEach((root) => {
      if (root.dataset.awDemandAnalyticsReady === 'true') return;
      const query = clean(root.dataset.searchTerm || '');
      const universe = root.querySelector('[data-aw-zero-universe]')?.dataset.awZeroUniverse || 'unknown';
      track('search_zero_result_recovery_shown', {
        query,
        universe
      }, `zero-shown|${query}|${universe}`);

      root.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (!link) return;
        const targetPath = pathOf(link.getAttribute('href') || '');
        if (targetPath === '/pages/compatibilite-airsoft') {
          track('search_zero_result_compatibility_clicked', {
            query,
            universe,
            target_path: targetPath
          });
        }
      }, true);
      root.dataset.awDemandAnalyticsReady = 'true';
    });
  };

  const evaluatePredictiveRecall = (overlay, panel) => {
    if (!overlay || !panel || panel.hidden) return;
    const input = overlay.querySelector('[data-search-input]');
    const query = clean(input?.value || '');
    if (!query) return;
    const recalledProducts = panel.querySelectorAll('[data-aw-predictive-recall]').length;
    const exactPages = panel.querySelectorAll('[data-aw-predictive-exact-page]').length;
    if (!recalledProducts && !exactPages) return;

    track('predictive_model_recall_exposed', {
      query,
      recalled_product_count: recalledProducts,
      exact_compatibility_page_count: exactPages,
      exact_page_available: exactPages > 0
    }, `recall-exposed|${query}|${recalledProducts}|${exactPages}`);
  };

  const bindPredictive = (overlay, panel) => {
    if (!overlay || !panel || panel.dataset.awDemandAnalyticsReady === 'true') return;
    const input = overlay.querySelector('[data-search-input]');
    let timer = null;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => evaluatePredictiveRecall(overlay, panel), 90);
    };

    new MutationObserver(schedule).observe(panel, {
      attributes: true,
      attributeFilter: ['hidden', 'data-aw-predictive-recall', 'data-aw-predictive-exact-page'],
      childList: true,
      subtree: true
    });
    input?.addEventListener('input', schedule, { passive: true });
    panel.addEventListener('click', (event) => {
      const option = event.target.closest('[data-aw-predictive-exact-page], [data-aw-predictive-recall]');
      if (!option) return;
      const link = option.matches('a[href]') ? option : option.querySelector('a[href]');
      const resultKind = option.matches('[data-aw-predictive-exact-page]') ? 'exact_compatibility_page' : 'recalled_product';
      const payload = {
        query: clean(input?.value || ''),
        result_kind: resultKind,
        target_path: pathOf(link?.getAttribute('href') || '')
      };
      track(
        resultKind === 'exact_compatibility_page'
          ? 'predictive_model_recall_compatibility_clicked'
          : 'predictive_model_recall_result_clicked',
        payload
      );
    }, true);
    panel.dataset.awDemandAnalyticsReady = 'true';
    schedule();
  };

  const initPredictive = () => {
    const overlay = document.querySelector('[data-search-overlay]');
    if (!overlay) return;
    const panel = overlay.querySelector('#PredictiveSearchPremium');
    if (panel) {
      bindPredictive(overlay, panel);
      return;
    }
    if (predictivePanelWatcher) return;
    predictivePanelWatcher = new MutationObserver(() => {
      const found = overlay.querySelector('#PredictiveSearchPremium');
      if (!found) return;
      predictivePanelWatcher.disconnect();
      predictivePanelWatcher = null;
      bindPredictive(overlay, found);
    });
    predictivePanelWatcher.observe(overlay, { childList: true, subtree: true });
  };

  const stateFor = (root) => {
    if (!rootState.has(root)) {
      rootState.set(root, {
        source: 'unknown',
        sourceAt: 0,
        query: '',
        detail: null,
        lastDepth: ''
      });
    }
    return rootState.get(root);
  };

  const sourceFor = (state) => Date.now() - state.sourceAt < 5000 ? state.source : 'unknown';

  const scheduleDepth = (root, reason = 'render') => {
    const state = stateFor(root);
    if (!state.detail) return;
    window.clearTimeout(state.depthTimer);
    state.depthTimer = window.setTimeout(() => {
      const totalNode = root.querySelector('[data-aw-total-count]');
      const loading = root.querySelector('[data-aw-catalog-loading]');
      const count = parseCount(totalNode);
      const catalogueReady = loading ? loading.hidden : true;
      const detail = state.detail || {};
      const signature = `${detail.modelLabel || detail.model || ''}|${count}|${catalogueReady}`;
      if (state.lastDepth === signature) return;
      state.lastDepth = signature;

      track('compatibility_catalog_depth_observed', {
        model_label: clean(detail.modelLabel || detail.model || ''),
        platform: clean(detail.platform || ''),
        manufacturer: clean(detail.manufacturerLabel || detail.manufacturer || ''),
        replica_type: clean(detail.replicaType || ''),
        activation_source: sourceFor(state),
        observation_reason: reason,
        catalog_correspondence_count: count,
        catalogue_ready: catalogueReady
      });

      if (catalogueReady && count === 0) {
        track('compatibility_catalog_zero_correspondence', {
          model_label: clean(detail.modelLabel || detail.model || ''),
          platform: clean(detail.platform || ''),
          activation_source: sourceFor(state)
        }, `compat-zero|${detail.modelLabel || detail.model || ''}|${detail.platform || ''}`);
      }
    }, 120);
  };

  const initCompatibilityRoot = (root) => {
    if (!root || root.dataset.awDemandAnalyticsReady === 'true') return;
    const state = stateFor(root);
    const searchInput = root.querySelector('[data-aw-search-input]');
    const searchForm = root.querySelector('[data-aw-search-form]');
    const manualForm = root.querySelector('[data-aw-manual-form]');
    const totalNode = root.querySelector('[data-aw-total-count]');
    const loading = root.querySelector('[data-aw-catalog-loading]');

    root.addEventListener('click', (event) => {
      const suggestion = event.target.closest('[data-aw-suggestion]');
      if (suggestion) {
        state.source = 'exact_suggestion';
        state.sourceAt = Date.now();
        state.query = clean(searchInput?.value || '');
        return;
      }

      const modelButton = event.target.closest('[data-aw-model-button]');
      if (modelButton) {
        state.source = 'model_button';
        state.sourceAt = Date.now();
        state.query = clean(searchInput?.value || '');
        return;
      }

      const productLink = event.target.closest('.aw-compatibility-search__product a[href]');
      if (productLink && state.detail) {
        const cards = [...root.querySelectorAll('.aw-compatibility-search__product')];
        const card = productLink.closest('.aw-compatibility-search__product');
        track('compatibility_result_product_clicked', {
          model_label: clean(state.detail.modelLabel || state.detail.model || ''),
          platform: clean(state.detail.platform || ''),
          rank_position: Math.max(1, cards.indexOf(card) + 1),
          target_path: pathOf(productLink.getAttribute('href') || '')
        });
      }
    }, true);

    searchForm?.addEventListener('submit', () => {
      state.source = 'search_submit';
      state.sourceAt = Date.now();
      state.query = clean(searchInput?.value || '');
      window.setTimeout(() => {
        const feedback = root.querySelector('[data-aw-search-feedback]');
        if (!feedback?.classList.contains('is-error')) return;
        const query = clean(searchInput?.value || state.query || '');
        const suggestionCount = root.querySelectorAll('[data-aw-suggestion]').length;
        track('compatibility_model_unrecognized', {
          query,
          suggestion_count: suggestionCount,
          feedback: clean(feedback.textContent || '', 220)
        }, `unrecognized|${query}|${suggestionCount}`);
      }, 140);
    }, true);

    manualForm?.addEventListener('submit', () => {
      state.source = 'manual_fallback';
      state.sourceAt = Date.now();
      const manufacturer = clean(manualForm.querySelector('[data-aw-manual-manufacturer]')?.value || '');
      const requestedModel = clean(manualForm.querySelector('[data-aw-manual-model]')?.value || '');
      const platform = clean(manualForm.querySelector('[data-aw-manual-platform]')?.value || '');
      const replicaType = clean(manualForm.querySelector('[data-aw-manual-type]')?.value || '');
      track('compatibility_manual_fallback_submitted', {
        requested_manufacturer: manufacturer,
        requested_model: requestedModel,
        platform,
        replica_type: replicaType,
        exact_model_proof: false
      });
    }, true);

    if (totalNode) {
      new MutationObserver(() => scheduleDepth(root, 'count_change')).observe(totalNode, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }
    if (loading) {
      new MutationObserver(() => scheduleDepth(root, 'catalogue_state_change')).observe(loading, {
        attributes: true,
        attributeFilter: ['hidden'],
        childList: true,
        subtree: true
      });
    }

    root.dataset.awDemandAnalyticsReady = 'true';
  };

  const findCompatibilityRootForDetail = (detail = {}) => {
    const roots = [...document.querySelectorAll('[data-aw-compatibility-search]')];
    if (!roots.length) return null;
    const label = normalized(detail.modelLabel || detail.model || '');
    return roots.find((root) => normalized(root.querySelector('[data-aw-search-input]')?.value || '') === label) || roots[0];
  };

  const onCompatibilityChanged = (event) => {
    const detail = event.detail || {};
    const root = findCompatibilityRootForDetail(detail);
    if (!root) return;
    initCompatibilityRoot(root);
    const state = stateFor(root);
    state.detail = detail;

    track('compatibility_profile_activated', {
      model: clean(detail.model || ''),
      model_label: clean(detail.modelLabel || ''),
      platform: clean(detail.platform || ''),
      platform_label: clean(detail.platformLabel || ''),
      manufacturer: clean(detail.manufacturerLabel || detail.manufacturer || ''),
      replica_type: clean(detail.replicaType || ''),
      activation_source: sourceFor(state),
      originating_query: clean(state.query || '')
    });
    scheduleDepth(root, 'profile_activated');
  };

  const initCompatibility = (scope = document) => {
    const roots = [];
    if (scope.matches?.('[data-aw-compatibility-search]')) roots.push(scope);
    roots.push(...scope.querySelectorAll?.('[data-aw-compatibility-search]') || []);
    roots.forEach(initCompatibilityRoot);
  };

  const initAll = (scope = document) => {
    initZeroRecovery(scope);
    initPredictive();
    initCompatibility(scope);
  };

  window.addEventListener('aw:compatibility-changed', onCompatibilityChanged);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  window.AWCompatibilityDemandAnalytics = {
    version: '1.0.0',
    governance: VERSION,
    mode: 'observe_only',
    init: initAll
  };
})();
